import * as React from 'react'
import {InputSelectProps, InputSelectMode} from './types'
import InputCheckbox from '../InputCheckbox'
import {findIndex} from 'lodash'
import {FixedSizeList as List} from 'react-window'
import {useTranslation} from 'react-i18next'
import Icon from '../Icon'
import Select from 'react-select'
import {createFilter} from 'react-select'
import {isBrowser} from '../../Utils'
import '@sass/_input-select.scss'
import {useEffect} from "react";


const InputSelect = (props: InputSelectProps) => {
    const {t} = useTranslation()
    const VirtualizedList = List as any

    const isMultiSelect = props.mode === InputSelectMode.Multiple
    const placeholder = props.placeholder || t('placeholder.selectDefault')
    const DropDownIndicator = () => <Icon iconName="dropdown" size={16}/>
    const isClearable = isMultiSelect ? false : !!props.clearable

    const renderMultiSelectOption = (option: any) => {
        if (option) {
            // cx is a react-select internal function and is needed for accessibility etc.
            const optionClassName =
                option.cx &&
                option.cx(
                    {
                        option: true,
                        'option--is-disabled': option.isDisabled,
                        'option--is-focused': option.isFocused,
                        'option--is-selected': option.isSelected,
                    },
                    ''
                )
            return (
                <div
                    ref={option.innerRef}
                    {...option.innerProps}
                    className={`input-select__multi-select-wrapper ${optionClassName}`}
                >
                    <InputCheckbox
                        id={`multi-select-option-${option.value}`}
                        label={option.label}
                        checked={option.isSelected}
                        onChange={() => {
                            /* Do nothing, react-select takes care of event handling */
                        }}
                    />
                </div>
            )
        }
        return null
    }

    const renderMultiPlaceholder = () => {
        if (props.customText) {
            return (
                <span className={`input-select__multi-placeholder ${props.theme || ''}`}>
                    {props.customText}
                </span>
            )
        } else {
            return (
                <span className={`input-select__multi-placeholder ${props.theme || ''}`}>
                    {props.label}
                </span>
            )
        }
    }

    const renderMultiValue = (multiValue: any) => {
        if (multiValue && multiValue.selectProps && multiValue.data) {
            const selectValues = multiValue.selectProps.value
            const thisIndex = findIndex(selectValues, {
                value: multiValue.data.value,
            })
            return thisIndex === 0 ? renderMultiPlaceholder() : null
        }
        return null
    }

    const customComponents: any = {
        DropdownIndicator: DropDownIndicator,
    }
    if (isMultiSelect) {
        customComponents.Option = renderMultiSelectOption
        customComponents.MultiValue = renderMultiValue
        customComponents.Placeholder = renderMultiPlaceholder
    } else if (props.performanceMode) {
        // Suitable when there is more than 1000 options available
        customComponents.MenuList = (props: any) => {
            const noHits =
                props.emptyMessage || t('message.selectDefaultEmptyMessage')
            const height = 50
            const {options, children, maxHeight, getValue} = props
            const [value] = getValue()
            let customMaxHeight = maxHeight
            let initialOffset = options.indexOf(value) * height
            const nrOfOptions = children.length || 0
            if (nrOfOptions < 6) {
                if (nrOfOptions === 0) {
                    customMaxHeight = height
                    initialOffset = 0
                } else {
                    customMaxHeight = nrOfOptions * height
                }
            }

            return (
                <VirtualizedList
                    height={customMaxHeight}
                    itemCount={nrOfOptions === 0 ? 1 : nrOfOptions}
                    itemSize={height}
                    width={'auto'}
                    initialScrollOffset={initialOffset}
                >
                    {({index, style}: any) => {
                        if (nrOfOptions === 0) {
                            return (
                                <div className='input-select__empty-message'>
                                    {noHits}
                                </div>
                            )
                        }
                        return <div style={style}>{children[index]}</div>
                    }}
                </VirtualizedList>
            )
        }
        customComponents.MenuList.displayName = 'MenuList'
    }

    return (
        <div className={`input-select ${props.className} ${props.theme}`}>
            <label
                className={`input-select__label ${props.hideLabel ? 'sr-only' : ''}`}
                htmlFor={props.id}
            >
                {props.label}
                {props.required && '*'}
            </label>
            <div
                className={`input-select__select-wrapper ${props.hasError ? 'error' : ''} ${props.theme ||
                ''}`}
            >
                <form autoComplete={'off'}>
                    <Select
                        className="panta-select-container"
                        classNamePrefix="panta-select"
                        closeMenuOnSelect={!isMultiSelect}
                        components={customComponents}
                        filterOption={createFilter({
                            ignoreAccents: !props.performanceMode,
                            stringify: option => {
                                if (props.customFilterMatching) {
                                    return `${option.data.data}`
                                }
                                return `${option.label} ${option.value}`
                            },
                        })}
                        hideSelectedOptions={!!props.hideSelectedOptions}
                        noOptionsMessage={() => props.emptyMessage ||
                            t('message.selectDefaultEmptyMessage')}
                        inputId={props.id}
                        isClearable={isClearable}
                        isDisabled={props.disabled}
                        isMulti={isMultiSelect}
                        isSearchable={props.allowInput}
                        value={props.value || null}
                        onChange={props.onChange}
                        options={props.options}
                        required={props.required}
                        aria-describedby={props.hasError && props.errorText ? `${props.id}-error` : undefined}
                        placeholder={placeholder}/>
                </form>
            </div>

            {props.hasError && props.errorText && (
                <div className="alert alert--danger alert--input mt-2" id={`${props.id}-error`}>
                    {props.errorText}
                </div>
            )}
        </div>
    )
}

export default InputSelect;
